import { Suspense, lazy } from "react";
import { Navigate, useRoutes, useLocation } from "react-router-dom";
// layouts
import DashboardLayout from "../layouts/dashboard";

// guards
import GuestGuard from "../guards/GuestGuard";
import AuthGuard from "../guards/AuthGuard";
// config
import { PATH_AFTER_LOGIN } from "../config";
// components
import LoadingScreen from "../components/LoadingScreen";
import VendorDashboard from "src/pages/vendor";
import GSTReport from "src/pages/reports/GSTReport";


// ----------------------------------------------------------------------

export const Loadable = Component => props => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  return (
    <Suspense fallback={<LoadingScreen />}>
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    {
      path: "auth",
      children: [
        {
          path: "login",
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
      ],
    },
    // Dashboard Routes
    {
      path: "dashboard",
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },
        { path: "app", element: <VendorDashboard /> },
        { path: "invoice", element: <InvoiceDetails /> },
        { path: "tickets", element: <ReportedIssue /> },
        { path: "raised-ticket", element: <RaisedTicket /> },
        { path: "student-sizing", element: <StudentSizing /> },
        { path: "variation-bulk", element: <OrderPromotion /> },

        {
          path: "master",
          children: [
            {
              element: <Navigate to="/dashboard/master/package" replace />,
              index: true,
            },
            { path: "category", element: <Category /> },
            { path: "package", element: <PackageMaster /> },
            { path: "product", element: <ProductMaster /> },  
            { path: "ticket-category", element: <TicketCategoryMaster /> },
          ],
        },
        {
          path: "reports",
          children: [
            {
              element: <Navigate to="/dashboard/reports/new-report" replace />,
              index: true,
            },
            { path: "new-report", element: <NewOrder /> },
            {
              path: "new-and-pending-report",
              element: <NewAndPendingReport />,
            },
            { path: "student-wise-pending-report", element: <StudentWisePendingOrders /> },
            { path: "school-wise-pending-report", element: <SchoolWisePendingOrders /> },
            { path: "date-wise-report", element: <OrderedProductReport /> },
            { path: "order-wise-gst-report", element: <OrderWiseGSTReport /> },
            { path: "sale-with-gst", element: <GSTReport type={"withGst"} /> },
            { path: "sale-without-gst-report", element: <GSTReport /> },
            { path: "sales-report", element: <SalesReport /> },
            { path: "settlement-report", element: <SettlementReport /> },
            {
              path: "shipping-wise-report",
              element: <GSTReport type={"shipping"} />,
            },
          ],
        },
      ],
    },

    // Main Routes

    {
      path: "/",
      element: (
        <GuestGuard>
          <Login />
        </GuestGuard>
      ),
    },
    { path: "*", element: <Navigate to="/404" replace /> },
  ]);
}

// AUTHENTICATION
const Login = Loadable(lazy(() => import("../pages/auth/Login")));


// DASHBOARD

// GENERAL
const InvoiceDetails = Loadable(
  lazy(() => import("src/pages/vendor/InvoiceDetails"))
);
const RaisedTicket = Loadable(
  lazy(() => import("src/pages/ticket/RaisedTicket"))
);
const StudentSizing = Loadable( lazy(() => import("src/pages/student-sizing")) );
const ReportedIssue = Loadable(lazy(() => import("src/pages/ticket")));
const OrderPromotion = Loadable(lazy(() => import("src/pages/variation-bulk")));


const NewAndPendingReport = Loadable(
  lazy(() => import("src/pages/reports/NewAndPendingReport"))
);
const OrderedProductReport = Loadable(
  lazy(() => import("src/pages/reports/OrderedProductReport"))
);
const OrderWiseGSTReport = Loadable(
  lazy(() => import("src/pages/reports/OrderWiseGSTReport"))
);
const NewOrder = Loadable(lazy(() => import("src/pages/reports/NewOrder")));
const SalesReport = Loadable(lazy(() => import("src/pages/reports/SalesReport")));
const SettlementReport = Loadable(lazy(() => import("src/pages/reports/SettlementReport")));
const StudentWisePendingOrders = Loadable(
  lazy(() => import("src/pages/reports/StudentWisePendingOrders"))
);
const SchoolWisePendingOrders = Loadable(
  lazy(() => import("src/pages/reports/SchoolwisePendingOrders"))
);
const Category = Loadable(lazy(() => import("src/pages/master/catgory")));
const ProductMaster = Loadable(
  lazy(() => import("src/pages/master/product-master"))
);
const TicketCategoryMaster = Loadable(
  lazy(() => import("src/pages/master/ticket-category"))
);
const PackageMaster = Loadable(
  lazy(() => import("src/pages/master/package-master"))
);
